import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.deviceActivityLog;
const queryKey = "deviceActivityLog";

async function fetchDeviceActivityLog(
  currentPage,
  search,
  employeeId,
  action,
  startTime,
  endTime,
  saleBlock,
  saleArea,
  department
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (employeeId) options.employeeId = employeeId;
    if (action) options.action = action;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);
    if (saleBlock) options.saleBlockId = saleBlock;
    if (saleArea) options.saleAreaId = saleArea;
    if (department) options.department = department;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/deviceActivityLog?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        code: item?.employeeObject?.code || item?.employee?.code || "-",
        name: item?.employeeObject?.fullName || item?.employee?.fullName || "-",
        actionDetail: item?.action || "-",
        deviceCode: item?.deviceCode || "-",
        deviceName:
          item?.deviceInfo?.modelName || item?.deviceInfo?.deviceName || "-",
        appVersion: item?.appVersion || "-",
        createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "-",
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useDeviceActivityLog(
  search,
  employeeId,
  action,
  startTime,
  endTime,
  saleBlockId,
  saleAreaId,
  department
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      employeeId,
      action,
      startTime,
      endTime,
      saleBlockId,
      saleAreaId,
      department,
    ],
    queryFn: () =>
      fetchDeviceActivityLog(
        currentPage,
        search,
        employeeId,
        action,
        startTime,
        endTime,
        saleBlockId,
        saleAreaId,
        department
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    deviceActivityLog: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}


async function fetchDeviceActivityWarning(
  currentPage,
  search,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search) options.search = search;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/deviceActivityLog/warning?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        employee: `${item?.employee?.fullName} (${item?.employee?.code})`,
        action: "Xem chi tiết",
        detail: {
          employee: item?.employee,
          employeeObjects: item?.employeeObjects,
          deviceInfo: item?.deviceInfo,
        }
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useDeviceActivityWarning(search) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
    ],
    queryFn: () =>
      fetchDeviceActivityWarning(
        currentPage,
        search,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    deviceActivityWarning: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
